import React, { useState } from 'react';
import * as yup from 'yup';
import {
  Section,
  Heading,
  Paragraph,
  HorizontalRule,
  IconPaymentsAndTransfers,
  IconSaving,
  IconEducation,
  IconPaymentInternational,
  IconBorrowing,
  FormLayout,
} from '@piggybank/core';
import {
  Form,
  FormFeedback,
  Field,
  Fieldset,
  FieldFeedback,
  Label,
  Hint,
  Select,
  TextInput,
  YesNoRadio,
  Radio,
  CurrencyInput,
  AddAnother,
  Legend,
  IconTile,
  TileGroup,
} from '@piggybank/form';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { accountPurpose as useAccountList, Country, funding } from 'referenceData';
import { WithI18nProgressIndicator } from 'components/WithI18nPiggybank';
import { useHistory } from 'react-router-dom';
import './custom.css';
import { ButtonWithBack } from 'components/ButtonWithBack';
import { setUserSessionDataByKey, getUserSessionDataByKey } from 'storage/userStorage';
import { rePrevious } from 'utils/util';
import { linkTagging, validationTagging, useTagging } from 'tealiumTrack';
import {
  EMPLOYMENT_STATUS_WORKING_OPTIONS,
  ANOTHER_SOURCE,
  TRANSFER_FROM_ANOTHER_ACCOUNT,
  ACCOUNT_SOURCE_FROM,
  NO,
  YES,
} from '../../../referenceData/constants';

const TAG_PAGE_URL = '/forms/using-your-account';
const TAG_PAGE_NAME = 'pws:forms:using your account';
const ICON_MAPPING = {
  EVERYDAY_BANKING: IconPaymentsAndTransfers,
  SAVING_INVESTMENTS: IconSaving,
  EDUCATIONAL_FUNDS: IconEducation,
  LOANS_MORTGAGE: IconBorrowing,
  INTERNATIONAL_PAYMENTS: IconPaymentInternational,
};

const fundSchema = yup.object().shape({
  fundsSource: yup.string().required(() => i18n.t('ICO.USING.SELECT_AN_OPTION')),
  depositSource: yup.array().when('fundsSource', {
    is: val => !!val,
    then: yup.array().min(1, () => i18n.t('ICO.USING.SELECT_AN_OPTION'))
  }),
  anotherSource: yup.string().when('depositSource', {
    is: val => Array.isArray(val) && val.includes(ANOTHER_SOURCE),
    then: yup.string()
      .required(() => i18n.t('ICO.USING.WILL_RECEIVING_FUNDS_OTHER_ERROR'))
      .max(150, () => i18n.t('ICO.USING.WILL_RECEIVING_FUNDS_OTHER_MAX_ERROR'))
      .matches(/^[\u4300-\u9fa5A-Za-z0-9()@&.\-/', ]*$/, () => i18n.t('ICO.USING.WILL_RECEIVING_FUNDS_OTHER_CHAR_ERROR'))
  }),
  otherNationality: yup.array().when('depositSource', {
    is: val => Array.isArray(val) && val.includes(TRANSFER_FROM_ANOTHER_ACCOUNT),
    then: yup.array().of(
      yup.string().required(() => i18n.t('ICO.USING.WILL_RECEIVING_FUNDS_ERROR'))
    )
  }),
  depositAmount: yup.string().when('depositSource', {
    is: val => Array.isArray(val) && val.includes(TRANSFER_FROM_ANOTHER_ACCOUNT),
    then: yup.string().required(() => i18n.t('ICO.USING.THIRD_AMOUNT_ERROR_MESSAGE'))
  }),
});

const getSchema = (fundSourceIndicator) => {
  const schemaShape = {
    selectAccountPurpose: yup.array().test(
      'selectAccountPurpose',
      () => i18n.t('ICO.USING.USE_ACCOUNT_ERROR_MESSAGE'),
      values => Array.isArray(values) && values.length > 0
    ),
    regularFundsDetailsObj: fundSchema,
    sourceSame: yup
      .string()
      .matches(/(yes|no)/)
      .required(() => i18n.t('ICO.USING.SELECT_AN_OPTION')),
    firstDepositDetailsObj: yup.object().when('sourceSame', {
      is: val => val === NO,
      then: fundSchema
    })
  };

  if (fundSourceIndicator === YES) {
    delete schemaShape.regularFundsDetailsObj;
  }

  return yup.object().shape(schemaShape);
}

const FundSource = ({ path, values, setFieldValue }) => {
  const { t, i18n } = useTranslation();
  const lan = i18n.language;
  const countryList = Country[lan];
  const [fundSourceList, setFundSourceList] = useState(funding[values[path].fundsSource] ?? []);
  const fundSourceLegend = path === 'regularFundsDetailsObj' ? 'ICO.USING.MONTHLY_FUNDS' : 'ICO.USING.FIRST_DEPOSIT_SOURCE';

  return (
    <>
      <Fieldset
        name={`${path}.fundsSource`}
        onChange={(next, { value, ...rest }) => {
          setFieldValue(`${path}.depositSource`, []);
          setFieldValue(`${path}.anotherSource`, '');
          setFieldValue(`${path}.otherNationality`, []);
          setFieldValue(`${path}.depositAmount`, '');
          setFundSourceList(funding[value] ?? []);
          next({ value, ...rest });
        }}>
        <Legend>{t(fundSourceLegend)}</Legend>
        <Paragraph hint>{t('ICO.USING.SELECT_AN_OPTION')}</Paragraph>
        {ACCOUNT_SOURCE_FROM.map((item) => (
          <Radio value={item.value} key={item.value}>{t(item.label)}</Radio>
        ))}
        <FieldFeedback />
      </Fieldset>
      {values[path].fundsSource && <>
        <Fieldset name={`${path}.depositSource`} marginBottom={4}
          onChange={(next, { value, ...rest }) => {
            if (values[path].fundsSource === 'THIRD_PARTY') {
              if (!value.includes(ANOTHER_SOURCE)) {
                setFieldValue(`${path}.anotherSource`, '');
              }
              if (!value.includes(TRANSFER_FROM_ANOTHER_ACCOUNT)) {
                setFieldValue(`${path}.otherNationality`, []);
                setFieldValue(`${path}.depositAmount`, '');
              }
            }
            next({ value, ...rest });
          }}>
          <Legend>{t('ICO.USING.MONEY_COME_FROM_WHERE')}</Legend>
          <Hint>{t('ICO.COMMON.SELECT_REMIND')}:</Hint>
          <TileGroup multiSelect >
            {fundSourceList.map((item) => (
              <IconTile fullWidth key={item.value} value={item.value} icon={IconEducation}>
                {item[lan]}
              </IconTile>
            ))}
          </TileGroup>
          <FieldFeedback marginTop={1} />
        </Fieldset>
        {values[path].depositSource?.includes(ANOTHER_SOURCE) && <Field name={`${path}.anotherSource`}>
          <Label>{t('ICO.USING.WILL_RECEIVING_FUNDS_OTHER')}</Label>
          <TextInput fullWidth />
          <FieldFeedback />
        </Field>}
      </>}
      {values[path].depositSource?.includes(TRANSFER_FROM_ANOTHER_ACCOUNT) && <div>
        <Label>{t('ICO.USING.WILL_RECEIVING_FUNDS')}</Label>
        <AddAnother
          name={`${path}.otherNationality`}
          itemInitialValue=''
          fullWidth
          max={7}
          min={1}
          minOnMount={1}
          renderAddButtonText={() => t('ICO.USING.ADD_COUNTRY')}
          renderRemoveButtonText={() => t('ICO.PERSONAL.ADD_ANOTHER_NATIONALITY_REMOVE')}
          marginBottom={3}
        >
          {({ path }) => (
            <Field name={path} marginBottom={0}>
              <Label>{t('ICO.USING.WILL_RECEIVING_FUNDS_LABEL')}</Label>
              <Select
                fullWidth
                options={countryList}
                placeholder={t('ICO.PERSONAL.COUNTRY_OF_BIRTH_PLACEHOLDER')}
              />
              <FieldFeedback />
            </Field>
          )}
        </AddAnother>
        <Field name={`${path}.depositAmount`}
          onChange={(next, { value, ...rest }) => {
            value = value.replace(/^0$/g, '');
            next({ value, ...rest });
          }}
        >
          <Label>{t('ICO.USING.THIRD_AMOUNT')}</Label>
          <Paragraph hint>{t('ICO.USING.THIRD_AMOUNT_DESC')}</Paragraph>
          <CurrencyInput maxLength={7} symbol={t('ICO.FINANCIAL.HKD1')} fullWidth />
          <FieldFeedback />
        </Field>
      </div>}
    </>
  )
}

const UsingAccount = () => {
  const initialValues = getUserSessionDataByKey('using');
  const { paidFromEmployerIndicator, employmentStatus } = getUserSessionDataByKey('financial');
  const { isPremier, enableCreditCard } = getUserSessionDataByKey('global');
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const lan = i18n.language;

  useTagging({
    'page_url': TAG_PAGE_URL,
    'page_language': lan,
    'page_name': TAG_PAGE_NAME,
    'page_subcategory': 'using your account',
    'raw_datalayer': '5745v17',
    'funnel_name': `account opening: ${isPremier ? 'premier' : 'one'}`,
    'funnel_step': '5',
    'funnel_step_name': 'using your account'
  });

  const taggingParams = {
    'page_url': TAG_PAGE_URL,
    'page_language': lan,
    'event_category': 'error',
    'event_action': 'field validation',
    'page_name': TAG_PAGE_NAME,
    'raw_datalayer': '5745v19'
  };

  return (
    <>
      <FormLayout>
        <WithI18nProgressIndicator current={4} />
        <Section>
          <Heading level={1}>{t('ICO.USING.TITLE')}</Heading>
          <Paragraph lead>{t('ICO.USING.SUB_TITLE')}</Paragraph>
          <HorizontalRule />
        </Section>
        <Form
          initialValues={initialValues}
          validationSchema={getSchema(paidFromEmployerIndicator)}
          onSubmit={({ values }) => {
            setUserSessionDataByKey('using', values);
            history.push(isPremier && enableCreditCard ? '/info/creditCard' : '/info/review');
          }}
          onChange={rePrevious}
        >
          {({ values, isSubmitting, isValid, errors, setFieldValue }) => {
            if (isSubmitting && !isValid && JSON.stringify(errors) !== '{}') {
              validationTagging({ errors, taggingParams });
            }
            return (
              <>
                <FormFeedback textMap={{ title: t('ICO.COMMON.CORRECT'), of: '/' }} />
                <Section>
                  <Heading level={2} accentBar>
                    {t('ICO.USING.ANTICIPATED_USAGE_SECTION')}
                  </Heading>
                  <Fieldset
                    name="selectAccountPurpose"
                    onChange={(next, { value, ...rest }) => {
                      linkTagging({
                        'page_url': TAG_PAGE_URL,
                        'page_language': lan,
                        'event_category': 'content',
                        'event_action': 'checkbox',
                        'event_content': `anticipated account usage: ${value.join(',')}`,
                        'page_name': TAG_PAGE_NAME,
                        'raw_datalayer': '5745v18'
                      });
                      next({ value, ...rest });
                    }}
                  >
                    <Legend>{t('ICO.USING.USE_ACCOUNT')}</Legend>
                    <Hint>{t('ICO.COMMON.SELECT_REMIND')}</Hint>
                      <TileGroup multiSelect>
                        {useAccountList.map((item) => (
                          <IconTile fullWidth key={item.value} value={item.value} icon={ICON_MAPPING[item.value]}>
                            {item[lan]}
                          </IconTile>
                        ))}
                      </TileGroup>
                    <FieldFeedback marginTop={1} />
                  </Fieldset>
                </Section>

                {/* Source of regular funds */}
                {paidFromEmployerIndicator !== YES && <Section className="funds-source-section">
                  <Heading level={2} accentBar>
                    {t('ICO.USING.ACCOUNT_FUNDING_SECTION')}
                  </Heading>
                  <FundSource path='regularFundsDetailsObj' values={values} setFieldValue={setFieldValue}/>
                </Section>}

                {/* First deposit */}
                <Section className="funds-source-section">
                  <Heading level={2} accentBar>
                    {t('ICO.USING.FIRST_DEPOSIT_SECTION')}
                  </Heading>
                  <Fieldset name="sourceSame">
                    <Label>
                      {EMPLOYMENT_STATUS_WORKING_OPTIONS.includes(employmentStatus) ? t('ICO.USING.SOURCE_SAME_EMPLOYMENT') : t('ICO.USING.SOURCE_SAME')}
                    </Label>
                    <YesNoRadio textMap={{ yes: t('ICO.COMMON.YES'), no: t('ICO.COMMON.NO') }} />
                    <FieldFeedback marginTop={1} />
                  </Fieldset>
                  {values.sourceSame === NO && 
                    <FundSource path='firstDepositDetailsObj' values={values} setFieldValue={setFieldValue}/>
                  }
                </Section>
                <ButtonWithBack />
              </>
            )
          }}
        </Form>
      </FormLayout>
    </>
  );
}

export default UsingAccount;
